/* eslint-disable @typescript-eslint/no-explicit-any */
import { isUnchangedEmailField } from '../_schemas/HcpSchemas/ContactSection.validation';

type AnyValue = any;

const METADATA_KEYS = new Set([
	'isPrimary',
	'isPrimaryRelationship_V',
	'primaryCda_V',
	'vid_V', // We omit this from dirty checks, but inject it manually if real changes exist
	'fieldName'
]);

export function getDirtyValues<T extends Record<string, AnyValue>>(
	dirtyFields: Record<string, AnyValue>,
	currentValues: T,
	defaultValues?: T
): Partial<T> {
	const getBackendId = (item: any) => item?.vid_V;

	// ARRAYS: Return an OBJECT keyed by index to preserve positions
	if (Array.isArray(dirtyFields) && Array.isArray(currentValues)) {
		const result: Record<string, AnyValue> = {};

		dirtyFields.forEach((dirtyItem, index) => {
			if (!dirtyItem) return;

			const currentItem = currentValues[index];
			const defaultItem = defaultValues?.[index];

			// Recursively get changes for this item
			const itemChanges = getDirtyValues(dirtyItem, currentItem, defaultItem);

			// If the item has ANY remaining keys after filtering metadata, it is valid
			if (Object.keys(itemChanges).length > 0) {
				const vid = getBackendId(currentItem);

				// Even though vid_V is in METADATA_KEYS (and thus filtered out above), we MUST inject it here if the item exists in the backend so the API knows what to update
				if (vid) {
					itemChanges.vid_V = vid;
				}

				// Store using the original INDEX as the key
				result[index] = itemChanges;
			}
		});

		return result as any;
	}

	// OBJECTS
	const result: AnyValue = {};

	if (!dirtyFields || typeof dirtyFields !== 'object') return result;

	Object.keys(dirtyFields).forEach(key => {
		// 1. STRICT METADATA FILTER - If this key is a metadata key, we ignore it completely (primarly done to hide the "Primary" flags)
		if (METADATA_KEYS.has(key)) return;

		const dirtyVal = dirtyFields[key];
		const currentVal = currentValues?.[key];
		const defaultVal = defaultValues?.[key];

		if (dirtyVal === true) {
			// Same idea as the METADATA_KEYS filter above, for email fields.
			if (isUnchangedEmailField(key, currentVal, defaultVal)) return;

			// Primitive value changed
			result[key] = currentVal;
		} else if (typeof dirtyVal === 'object' && dirtyVal !== null) {
			// Check for deletion (if current is null/undefined but default existed)
			if ((currentVal === undefined || currentVal === null) && defaultVal) {
				result[key] = { _deleted: true };
				return;
			}

			const nested = getDirtyValues(dirtyVal, currentVal, defaultVal);

			// Only include the nested object if it has real data inside
			if (Object.keys(nested).length > 0) {
				if (!Array.isArray(nested)) {
					const vid = getBackendId(currentVal);
					if (vid) {
						nested.vid_V = vid;
					}
				}
				result[key] = nested;
			}
		}
	});

	return result;
}
